/**
 * TanStack Query Hook for Jobs Created Trends KPI
 *
 * This hook provides access to jobs created trends KPI data from the database,
 * including total jobs created and year-over-year percentage change.
 *
 * Database Source: web.v_kpi_jobs_created_trends_v1 view
 *
 * @module useKpiJobsCreatedTrendsV1
 * @category Database Hooks
 */

import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

/**
 * Interface for Jobs Created Trends KPI data
 */
interface KpiJobsCreatedTrends {
  jobs_created: number;
  jobs_created_change_percentage: number;
}

/**
 * Fetches jobs created trends KPI data from the database
 *
 * @returns {Promise<KpiJobsCreatedTrends>} KPI data object with jobs count and change percentage
 * @throws {Error} If the database query fails or returns invalid data
 * @internal
 */
const fetchKpiJobsCreatedTrends = async (): Promise<KpiJobsCreatedTrends> => {
  const { data, error } = await supabase
    .schema('web')
    .from('v_kpi_jobs_created_trends_v1')
    .select('*')
    .single();

  if (error) {
    throw new Error(`Failed to fetch jobs created trends KPI: ${error.message}`);
  }

  if (!data) {
    throw new Error('No data returned from jobs created trends KPI query');
  }

  return data;
};

/**
 * React Hook for Jobs Created Trends KPI Data
 *
 * Fetches and caches the jobs created trends KPI data using TanStack Query.
 * Includes total jobs created and YoY change percentage.
 *
 * Query Configuration:
 * - Query Key: ['kpi', 'jobs-created-trends', 'v1']
 * - Stale Time: 1 hour (3600000ms)
 * - Refetch on Window Focus: false
 * - Refetch on Mount: false
 * - Retry Attempts: 2
 *
 * @returns {UseQueryResult<KpiJobsCreatedTrends>} TanStack Query result object
 *
 * @example
 * ```typescript
 * const { data, isLoading, isError } = useKpiJobsCreatedTrendsV1();
 *
 * if (isLoading) return <Spinner />;
 * if (isError) return <ErrorMessage />;
 *
 * return (
 *   <div>
 *     <p>Jobs Created: {data.jobs_created.toLocaleString()}</p>
 *     <p>Change: {data.jobs_created_change_percentage}%</p>
 *   </div>
 * );
 * ```
 */
export const useKpiJobsCreatedTrendsV1 = (): UseQueryResult<KpiJobsCreatedTrends> => {
  return useQuery<KpiJobsCreatedTrends>({
    queryKey: ['kpi', 'jobs-created-trends', 'v1'],
    queryFn: fetchKpiJobsCreatedTrends,
    staleTime: 3600000, // 1 hour in milliseconds
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 2,
  });
};
